import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Calculator from './Calculator';
import QuestionNavigator from './QuestionNavigator';
import SectionedNavigator from './SectionedNavigator';
import { useKeyboardShortcuts, optionLetter } from '../utils/keyboardShortcuts';
import { groupIntoSections, totalTimeSeconds, locateFlatIndex, flattenIndex, formatDuration, formatMinutes } from '../utils/examSections';
import './ExamMode.css';

function MiniMockExam({ mockId, user, onBack, onShowResults, apiUrl }) {
  const isStaff = user?.role === 'admin' || user?.role === 'tutor';
  const [questions, setQuestions] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attemptId, setAttemptId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  // In-progress picks for 'five_statement' questions (see PracticeMode.jsx
  // for the format). answers[id] only gets set once all five are picked,
  // so it's only then that we POST the saved answer to the server.
  const [fiveStatementDraft, setFiveStatementDraft] = useState({});
  const [examStarted, setExamStarted] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showNavigator, setShowNavigator] = useState(false);
  const [pendingResume, setPendingResume] = useState(null);
  const [checkedResume, setCheckedResume] = useState(isStaff);
  const [showExplanation, setShowExplanation] = useState(false);

  // Staff browse mode: flat index across all questions, no sections/timer.
  const [staffIndex, setStaffIndex] = useState(0);

  // Student exam mode: sequential sections, each independently timed.
  const [sectionIndex, setSectionIndex] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [sectionTimeRemaining, setSectionTimeRemaining] = useState(0);
  const [globalTimeRemaining, setGlobalTimeRemaining] = useState(0);
  const [sectionNotice, setSectionNotice] = useState('');

  useEffect(() => {
    fetchExamQuestions();
    if (!isStaff) checkActiveAttempt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mockId]);

  const fetchExamQuestions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/mini-mocks/${mockId}/questions`);
      setQuestions(response.data);
      setSections(groupIntoSections(response.data));
    } catch (err) {
      setError('Failed to load exam questions: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const checkActiveAttempt = async () => {
    try {
      const response = await axios.get(`${apiUrl}/mini-mocks/attempts/active?mini_mock_id=${mockId}`);
      if (response.data) setPendingResume(response.data);
    } catch (err) {
      // no active attempt — proceed with a fresh start
    } finally {
      setCheckedResume(true);
    }
  };

  const startExam = async () => {
    try {
      const response = await axios.post(`${apiUrl}/mini-mocks/attempts`, { mini_mock_id: mockId });
      setAttemptId(response.data.id);
      setSectionIndex(0);
      setQuestionIndex(0);
      setSectionTimeRemaining(sections[0]?.timeLimitSeconds || 0);
      setGlobalTimeRemaining(totalTimeSeconds(sections));
      setExamStarted(true);
    } catch (err) {
      setError('Failed to start exam: ' + (err.response?.data?.error || err.message));
    }
  };

  const resumeExam = () => {
    const attempt = pendingResume;
    const { sectionIndex: sIdx, questionIndex: qIdx } = locateFlatIndex(sections, attempt.current_question_index || 0);
    setAttemptId(attempt.id);
    setAnswers(attempt.saved_answers || {});
    setSectionIndex(sIdx);
    setQuestionIndex(qIdx);
    setSectionTimeRemaining(sections[sIdx]?.timeLimitSeconds || 0);
    const elapsed = Math.floor((Date.now() - new Date(attempt.started_at).getTime()) / 1000);
    setGlobalTimeRemaining(Math.max(0, totalTimeSeconds(sections) - elapsed));
    setPendingResume(null);
    setExamStarted(true);
  };

  // Section timer — auto-advances to the next section (or finishes) at zero.
  useEffect(() => {
    if (!examStarted || isStaff || sectionTimeRemaining <= 0) return;
    const timer = setInterval(() => setSectionTimeRemaining((t) => t - 1), 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examStarted, isStaff, sectionTimeRemaining]);

  useEffect(() => {
    if (!examStarted || isStaff) return;
    if (sectionTimeRemaining === 0) advanceSection(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionTimeRemaining]);

  // Global timer — hard stop for the whole exam, independent of section.
  useEffect(() => {
    if (!examStarted || isStaff || globalTimeRemaining <= 0) return;
    const timer = setInterval(() => setGlobalTimeRemaining((t) => t - 1), 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examStarted, isStaff, globalTimeRemaining]);

  useEffect(() => {
    if (!examStarted || isStaff) return;
    if (globalTimeRemaining === 0) handleSubmitExam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalTimeRemaining]);

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
    if (!attemptId) return; // staff browsing — nothing to save
    axios.post(`${apiUrl}/mini-mocks/answers`, {
      attempt_id: attemptId,
      question_id: questionId,
      selected_answer: answer,
      time_spent_seconds: 0
    }).catch((err) => console.error('Failed to save answer:', err));
  };

  const currentQuestion = isStaff ? questions[staffIndex] : sections[sectionIndex]?.questions[questionIndex];

  const handleSelectByLetter = (letter) => {
    const opts = getOptions(currentQuestion);
    const match = opts.find((o) => optionLetter(o) === letter);
    if (match) handleAnswerSelect(currentQuestion.id, match);
  };

  const handleToggleFlag = () => {
    if (!currentQuestion) return;
    setFlagged((prev) => ({ ...prev, [currentQuestion.id]: !prev[currentQuestion.id] }));
  };

  // Moves to the next section (or finishes if this was the last one).
  // auto=true means the section timer ran out — no confirmation needed.
  const advanceSection = (auto) => {
    const isLastSection = sectionIndex >= sections.length - 1;
    if (isLastSection) {
      handleSubmitExam();
      return;
    }
    if (!auto) {
      const nextName = sections[sectionIndex + 1].name;
      const ok = window.confirm(`Move to ${nextName}? You won't be able to return to ${sections[sectionIndex].name} once you continue.`);
      if (!ok) return;
    }
    const nextIndex = sectionIndex + 1;
    setSectionIndex(nextIndex);
    setQuestionIndex(0);
    setSectionTimeRemaining(sections[nextIndex].timeLimitSeconds);
    setShowExplanation(false);
    if (auto) {
      setSectionNotice(`Time's up for ${sections[sectionIndex].name} — moved on to ${sections[nextIndex].name}.`);
      setTimeout(() => setSectionNotice(''), 6000);
    }
  };

  const handleNext = () => {
    if (isStaff) {
      if (staffIndex < questions.length - 1) {
        setShowExplanation(false);
        setStaffIndex(staffIndex + 1);
      }
      return;
    }
    const section = sections[sectionIndex];
    if (questionIndex < section.questions.length - 1) {
      setShowExplanation(false);
      setQuestionIndex(questionIndex + 1);
    } else {
      advanceSection(false);
    }
  };

  const handlePrevious = () => {
    if (isStaff) {
      if (staffIndex > 0) {
        setShowExplanation(false);
        setStaffIndex(staffIndex - 1);
      }
      return;
    }
    // Matches the real UCAT rule: no going back once a section is left.
    if (questionIndex > 0) {
      setShowExplanation(false);
      setQuestionIndex(questionIndex - 1);
    }
  };

  useEffect(() => {
    if (!attemptId || !examStarted || isStaff) return;
    const flat = flattenIndex(sections, sectionIndex, questionIndex);
    const handle = setTimeout(() => {
      axios.patch(`${apiUrl}/mini-mocks/attempts/${attemptId}/progress`, {
        current_question_index: flat,
        saved_answers: answers,
      }).catch(() => {});
    }, 600);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId, examStarted, sectionIndex, questionIndex, answers]);

  const handleSubmitExam = async () => {
    if (arguments.length === 0) {
      // called directly (toolbar button / manual finish) — confirm first
    }
    try {
      const response = await axios.post(
        `${apiUrl}/mini-mocks/attempts/${attemptId}/complete`,
        { duration_seconds: totalTimeSeconds(sections) - globalTimeRemaining }
      );
      onShowResults({
        type: 'mini-mock',
        attempt: response.data,
        totalQuestions: questions.length,
        answeredCount: Object.keys(answers).length,
        score: response.data.score
      });
    } catch (err) {
      console.error('Failed to submit exam:', err);
      setError('Failed to submit exam');
    }
  };

  const handleFinishClick = () => {
    if (window.confirm('Finish this exam now? You cannot change your answers after this — any unanswered questions stay unanswered and count against your score.')) {
      handleSubmitExam();
    }
  };

  const getOptions = (question) => {
    if (!question || !question.options) return [];
    return typeof question.options === 'string' ? JSON.parse(question.options) : question.options;
  };

  const getFiveStatementValues = (question) => {
    if (!question) return [];
    if (answers[question.id]) {
      try {
        return JSON.parse(answers[question.id]);
      } catch {
        return [];
      }
    }
    return fiveStatementDraft[question.id] || [];
  };

  const handleFiveStatementSelect = (question, idx, value) => {
    const optsLen = getOptions(question).length;
    const current = getFiveStatementValues(question);
    const updated = current.length === optsLen ? [...current] : new Array(optsLen).fill(null);
    updated[idx] = value;
    setFiveStatementDraft((prev) => ({ ...prev, [question.id]: updated }));
    if (updated.every((v) => v === 'Yes' || v === 'No')) {
      const serialized = JSON.stringify(updated);
      setAnswers((prev) => ({ ...prev, [question.id]: serialized }));
      if (!attemptId) return;
      axios.post(`${apiUrl}/mini-mocks/answers`, {
        attempt_id: attemptId,
        question_id: question.id,
        selected_answer: serialized,
        time_spent_seconds: 0,
      }).catch((err) => console.error('Failed to save answer:', err));
    }
  };

  const currentOptions = getOptions(currentQuestion);

  useKeyboardShortcuts({
    enabled: (isStaff || examStarted) && Boolean(currentQuestion) && !showCalculator && !showNavigator,
    onNext: handleNext,
    onPrevious: handlePrevious,
    onFlag: handleToggleFlag,
    onToggleCalculator: () => setShowCalculator((v) => !v),
    onToggleNavigator: () => setShowNavigator((v) => !v),
    onSelectOption: handleSelectByLetter,
    optionLetters: currentQuestion?.answer_format === 'five_statement' ? [] : currentOptions.map(optionLetter).filter(Boolean),
  });

  if (loading || !checkedResume) {
    return (
      <div className="container">
        <div className="header"><h1>Mini Mock Exam</h1></div>
        <div className="content"><div className="loading"><div className="spinner"></div>Loading exam...</div></div>
      </div>
    );
  }

  if (!isStaff && !examStarted && pendingResume) {
    const { sectionIndex: sIdx, questionIndex: qIdx } = locateFlatIndex(sections, pendingResume.current_question_index || 0);
    return (
      <div className="container">
        <div className="header"><h1>Mini Mock Exam</h1></div>
        <div className="content">
          <div className="exam-intro">
            <h2>You have an unfinished Mini Mock Exam</h2>
            <div className="exam-details">
              <p>You were on <strong>{sections[sIdx]?.name}</strong>, question <strong>{qIdx + 1}</strong> of {sections[sIdx]?.questions.length},
                with <strong>{Object.keys(pendingResume.saved_answers || {}).length}</strong> answered overall.</p>
            </div>
            <div className="exam-actions">
              <button className="btn btn-secondary" onClick={() => setPendingResume(null)}>Start New</button>
              <button className="btn btn-primary btn-lg" onClick={resumeExam}>Resume</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isStaff && !examStarted) {
    return (
      <div className="container">
        <div className="header"><h1>Mini Mock Exam</h1></div>
        <div className="content">
          <div className="exam-intro">
            <h2>Ready to start your Mini Mock Exam?</h2>
            <p className="exam-intro-note">This exam consists of {sections.length} sections, timed separately, in the order shown below. You cannot return to a section once you move on.</p>
            <table className="section-table">
              <thead>
                <tr><th>Section</th><th>Questions</th><th>Time</th></tr>
              </thead>
              <tbody>
                {sections.map((s) => (
                  <tr key={s.code}>
                    <td>{s.name}</td>
                    <td>{s.questions.length}</td>
                    <td>{formatMinutes(s.timeLimitSeconds)}</td>
                  </tr>
                ))}
                <tr className="section-table-total">
                  <td>Total</td>
                  <td>{questions.length}</td>
                  <td>{formatMinutes(totalTimeSeconds(sections))}</td>
                </tr>
              </tbody>
            </table>
            <div className="exam-rules">
              <h3>Important Rules:</h3>
              <ul>
                <li>Each section has its own timer — when it runs out, you'll automatically move to the next section</li>
                <li>You cannot go back to a previous section once you've left it</li>
                <li>All answers are automatically saved as you go</li>
                <li>You can finish the whole exam early at any time from the toolbar</li>
              </ul>
            </div>
            <div className="exam-actions">
              <button className="btn btn-secondary" onClick={onBack}>Back to Dashboard</button>
              <button className="btn btn-primary btn-lg" onClick={startExam}>Start Exam</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (questions.length === 0 || !currentQuestion) {
    return (
      <div className="container">
        <div className="header"><h1>Mini Mock Exam</h1></div>
        <div className="content">
          <div className="error">No questions available for this exam.</div>
          <button className="btn btn-primary" onClick={onBack}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const isAnswered = answers[currentQuestion.id];
  const isFlagged = flagged[currentQuestion.id];
  const currentSection = isStaff ? null : sections[sectionIndex];
  const isLastQuestionOverall = isStaff
    ? staffIndex === questions.length - 1
    : sectionIndex === sections.length - 1 && questionIndex === currentSection.questions.length - 1;

  return (
    <div className="container exam-mode">
      <div className="header">
        <div>
          <h1>
            {isStaff
              ? `Mini Mock Exam - Question ${staffIndex + 1} of ${questions.length}`
              : `${currentSection.name} (Section ${sectionIndex + 1} of ${sections.length}) - Question ${questionIndex + 1} of ${currentSection.questions.length}`}
          </h1>
          {isStaff && <p className="header-subtitle">Browsing, not tracked as an attempt</p>}
        </div>
        {!isStaff && (
          <div className="header-timers">
            <div className={`timer ${sectionTimeRemaining < 60 ? 'danger' : sectionTimeRemaining < 180 ? 'warning' : ''}`}>
              Section: {formatDuration(sectionTimeRemaining)}
            </div>
            <div className="timer timer-global">
              Overall: {formatDuration(globalTimeRemaining)}
            </div>
          </div>
        )}
      </div>

      <div className="content">
        {sectionNotice && <div className="success">{sectionNotice}</div>}

        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: isStaff
              ? `${((staffIndex + 1) / questions.length) * 100}%`
              : `${((questionIndex + 1) / currentSection.questions.length) * 100}%` }}
          ></div>
        </div>

        <div className="exam-toolbar">
          <button className="toolbar-btn" onClick={() => setShowCalculator(true)}>🧮 Calculator</button>
          <button className="toolbar-btn" onClick={() => setShowNavigator(true)}>🗺️ Navigator</button>
          <button className={`toolbar-btn ${isFlagged ? 'toolbar-btn-active' : ''}`} onClick={handleToggleFlag}>
            {isFlagged ? '🚩 Flagged' : '🏳️ Flag question'}
          </button>
          {isStaff && (
            <button className="toolbar-btn" onClick={onBack}>🏠 Back to Dashboard</button>
          )}
          {!isStaff && (
            <button className="toolbar-btn toolbar-btn-finish" onClick={handleFinishClick}>✔ Finish Exam</button>
          )}
        </div>

        <div className="question-container">
          {currentQuestion.diagram_svg && (
            <div
              className="question-diagram"
              dangerouslySetInnerHTML={{ __html: currentQuestion.diagram_svg }}
            />
          )}
          {currentQuestion.passage_text && (
            <div className="passage-box">
              {currentQuestion.passage_title && <h4>{currentQuestion.passage_title}</h4>}
              <p>{currentQuestion.passage_text}</p>
            </div>
          )}
          {currentQuestion.scenario_text && (
            <div className="passage-box">
              {currentQuestion.scenario_title && <h4>{currentQuestion.scenario_title}</h4>}
              <p>{currentQuestion.scenario_text}</p>
            </div>
          )}

          <h3 className="question-text">{currentQuestion.question_text}</h3>

          {currentQuestion.answer_format === 'five_statement' ? (
            <div className="five-statement-set">
              {(() => {
                let correctValues = [];
                try {
                  correctValues = JSON.parse(currentQuestion.correct_answer || '[]');
                } catch {
                  correctValues = [];
                }
                const selectedValues = getFiveStatementValues(currentQuestion);
                return currentOptions.map((statementText, idx) => {
                  const selected = selectedValues[idx] || null;
                  const correctVal = correctValues[idx];
                  return (
                    <div key={idx} className="five-statement-row">
                      <div className="five-statement-text">{statementText}</div>
                      <div className="five-statement-buttons">
                        {['Yes', 'No'].map((val) => {
                          const isSel = selected === val;
                          const isCorrectVal = showExplanation && val === correctVal;
                          return (
                            <button
                              key={val}
                              type="button"
                              className={[
                                'five-statement-btn',
                                isSel && !showExplanation ? 'selected' : '',
                                isCorrectVal ? 'correct' : '',
                              ].join(' ').trim()}
                              disabled={showExplanation}
                              onClick={() => handleFiveStatementSelect(currentQuestion, idx, val)}
                            >
                              {val}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <div className="options">
              {currentOptions.map((option, idx) => {
                const isCorrectOption = showExplanation && option === currentQuestion.correct_answer;
                return (
                  <div
                    key={idx}
                    className={[
                      'option',
                      isAnswered === option && !showExplanation ? 'selected' : '',
                      isCorrectOption ? 'correct' : '',
                    ].join(' ').trim()}
                    onClick={() => handleAnswerSelect(currentQuestion.id, option)}
                  >
                    {option}
                  </div>
                );
              })}
            </div>
          )}

          {isStaff && !showExplanation && (
            <button className="btn btn-primary explain-btn" onClick={() => setShowExplanation(true)}>See Answer</button>
          )}
          {isStaff && showExplanation && (
            <div className="explanation">
              {currentQuestion.answer_format === 'five_statement' ? (
                <>
                  <strong>Correct answers shown above (green = correct).</strong>
                  <br />
                </>
              ) : (
                <>
                  <strong>Correct answer:</strong> {currentQuestion.correct_answer}
                  <br />
                </>
              )}
              <strong>Explanation:</strong> {currentQuestion.explanation}
            </div>
          )}
        </div>

        <div className="exam-navigation">
          <button
            className="btn btn-secondary"
            onClick={handlePrevious}
            disabled={isStaff ? staffIndex === 0 : questionIndex === 0}
          >
            ← Previous
          </button>

          <div className="nav-stats">
            <span>{isStaff ? `Question ${staffIndex + 1} of ${questions.length}` : `Answered: ${Object.keys(answers).length} / ${questions.length}`}</span>
          </div>

          {isLastQuestionOverall && !isStaff ? (
            <button className="btn btn-primary" onClick={handleFinishClick}>Finish Exam</button>
          ) : (
            <button className="btn btn-primary" onClick={handleNext} disabled={isStaff && staffIndex === questions.length - 1}>
              {!isStaff && questionIndex === currentSection.questions.length - 1 ? 'Next Section →' : 'Next →'}
            </button>
          )}
        </div>

        {error && <div className="error">{error}</div>}
      </div>

      {showCalculator && <Calculator onClose={() => setShowCalculator(false)} />}
      {showNavigator && isStaff && (
        <QuestionNavigator
          questions={questions}
          currentIndex={staffIndex}
          answers={answers}
          flagged={flagged}
          onJump={(idx) => { setShowExplanation(false); setStaffIndex(idx); }}
          onClose={() => setShowNavigator(false)}
        />
      )}
      {showNavigator && !isStaff && (
        <SectionedNavigator
          sections={sections}
          sectionIndex={sectionIndex}
          questionIndex={questionIndex}
          answers={answers}
          flagged={flagged}
          onJump={(sIdx, qIdx) => { setShowExplanation(false); setQuestionIndex(qIdx); }}
          onClose={() => setShowNavigator(false)}
        />
      )}
    </div>
  );
}

export default MiniMockExam;
